import { PHOTO_PLACEHOLDER, photoUrl, type PhotoSize } from '@platewell/query';
import { useEffect, useRef, useState } from 'react';
import recipePhotoPlaceholder from '../assets/recipe-photo-placeholder.svg';
import { API_BASE_URL } from '../lib/api.js';
import { localPreviewUrl } from '../lib/uploads.js';

export interface PhotoProps {
  blobId: string | null;
  alt: string;
  size?: PhotoSize;
  className?: string;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
}

/**
 * A photo that tolerates its bytes not existing yet.
 *
 * Between capturing a photo offline and the upload completing, the blob id is real but the
 * object is not, and other devices in the household will see the id first. Falling back to
 * the local copy and then to a neutral placeholder keeps that normal state from looking
 * like a broken image. Recipes with no photo show a place-setting graphic in list views, and
 * the same graphic marked "Loading image…" sits behind a photo until its bytes arrive.
 */
export function Photo({
  blobId,
  alt,
  size = 400,
  className,
  loading = 'lazy',
  onLoad,
}: PhotoProps) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLImageElement>(null);
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  useEffect(() => {
    setFailed(false);
    setLoaded(false);
  }, [blobId]);

  const local = blobId == null ? null : localPreviewUrl(blobId);
  const src =
    blobId == null
      ? recipePhotoPlaceholder
      : failed
        ? (local ?? PHOTO_PLACEHOLDER)
        : photoUrl(API_BASE_URL, blobId, size);

  useEffect(() => {
    const image = ref.current;
    if (image != null && image.complete && image.naturalWidth > 0) {
      setLoaded(true);
      onLoadRef.current?.();
    }
  }, [blobId, src]);

  if (blobId == null) {
    return <img className={className} src={recipePhotoPlaceholder} alt="" aria-hidden />;
  }

  function settle() {
    setLoaded(true);
    onLoadRef.current?.();
  }

  return (
    <img
      ref={ref}
      className={loaded ? className : `${className ?? ''} photo-loading`.trim()}
      src={src}
      alt={alt}
      loading={loading}
      decoding="async"
      onLoad={settle}
      onError={() => {
        setFailed(true);
        settle();
      }}
    />
  );
}

/** Hero photo for recipe detail — reserves layout space and shows a skeleton while loading. */
export function RecipeHeroPhoto({
  blobId,
  alt,
  width,
  height,
}: {
  blobId: string;
  alt: string;
  width: number;
  height: number;
}) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => setLoaded(false), [blobId]);

  const aspectRatio = width > 0 && height > 0 ? `${width} / ${height}` : '4 / 3';

  return (
    <div
      className={`recipe-hero-frame${loaded ? ' is-loaded' : ''}`}
      style={{ aspectRatio }}
    >
      <div className="recipe-hero-skeleton" aria-hidden />
      <Photo
        className="recipe-hero"
        blobId={blobId}
        alt={alt}
        size={1200}
        loading="eager"
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
}
